import app from 'flarum/forum/app';
import { extend, override } from 'flarum/common/extend';
import Button from 'flarum/common/components/Button';
import punctuateSeries from 'flarum/common/helpers/punctuateSeries';
import username from 'flarum/common/helpers/username';
import classList from 'flarum/common/utils/classList';
import extractText from 'flarum/common/utils/extractText';
import CommentPost from 'flarum/forum/components/CommentPost';

// What fof/reactions and flarum/likes add to the models.

/**
 * fof/reactions' reactions under a post are pills (reactions.less) with
 * their count, which fof only writes out from 2 up: a lone ❤️ read as a
 * stray grey dot. Each pill gets its count as data-count, shown by the
 * stylesheet whatever it is, and its label ("Reacted with heart") as a
 * tooltip.
 *
 * With fof's likes integration (a reaction, 👍, that is saved as a like),
 * likes are shown as that reaction too: a 👍 pill among the others instead
 * of flarum/likes' "You like this." line, and picking 👍 likes or unlikes
 * the post as the Like button does.
 */
export default function reactions() {
  extend('ext:fof/reactions/forum/components/PostReactionCounts', 'view', function (vnode) {
    const counts = this.attrs.post.reactionCounts() || {};
    const byIdentifier = new Map(app.store.all('reactions').map(reaction => [reaction.identifier(), reaction.id()]));
    const list = vnode?.children;
    const chips = list?.[0]?.children || [];
    chips.forEach(chip => {
      if (!chip?.attrs) return;
      const id = byIdentifier.get(String(chip.attrs['data-reaction']));
      const title = extractText(chip.attrs['aria-label']);
      chip.attrs['data-count'] = id ? counts[id] : undefined;
      chip.attrs.title = title;

      // The emoji has its own title (the bare identifier), which would win.
      const emoji = chip.children?.[0]?.children?.[0];
      if (emoji?.attrs) emoji.attrs.title = title;
    });
  });
}

/** The reaction fof/reactions saves as a like, if its likes integration is on. */
function likeReaction() {
  if (!('flarum-likes' in flarum.extensions)) return null;
  const identifier = app.forum.attribute('ReactionConverts')?.[2];
  if (!identifier) return null;
  return app.store.all('reactions').find(reaction => reaction.identifier() === identifier && reaction.enabled()) || null;
}
function likedByMe(post) {
  const likes = post.likes();
  return !!app.session.user && !!likes && likes.some(user => user === app.session.user);
}

/** Like or unlike, as flarum/likes' Like button does, showing it at once. */
function toggleLike(post) {
  const user = app.session.user;
  if (!user) return;
  const liked = !likedByMe(post);
  post.save({
    isLiked: liked
  });

  // Saved, but shown only once the response is back: change the loaded likes
  // and count now, as flarum/likes does.
  post.data.relationships = post.data.relationships || {};
  const relationships = post.data.relationships;
  relationships.likes = relationships.likes || {
    data: []
  };
  const data = relationships.likes.data;
  const index = data.findIndex(like => like.id === user.id());
  if (index !== -1) data.splice(index, 1);
  if (liked) data.unshift({
    type: 'users',
    id: String(user.id())
  });
  post.pushAttributes({
    likesCount: Math.max(0, (post.likesCount() || 0) + (liked ? 1 : -1))
  });
}

/** The likes as a reaction pill ("👍 3"), titled with who liked it. */
function likePill(post, reaction) {
  const likes = post.likes() || [];
  const count = Math.max(post.likesCount() || 0, likes.length);
  if (!count) return null;
  const liked = likedByMe(post);
  const names = [...likes].sort(a => a === app.session.user ? -1 : 1).slice(0, 3).map(user => extractText(user === app.session.user ? app.translator.trans('flarum-likes.forum.post.you_text') : username(user)));
  if (count > names.length) {
    names.push(extractText(app.translator.trans('flarum-likes.forum.post.others_link', {
      count: count - names.length
    })));
  }
  const title = extractText(app.translator.trans(`flarum-likes.forum.post.liked_by${liked ? '_self' : ''}_text`, {
    count: names.length,
    users: punctuateSeries(names)
  }));
  const ReactionComponent = flarum.reg.get('fof-reactions', 'common/components/ReactionComponent');
  const PostLikesModal = flarum.reg.get('flarum-likes', 'forum/components/PostLikesModal');
  return m("div", {
    className: "Reactions BdLikeReaction"
  }, m("div", {
    className: "Reactions--reactions"
  }, m(Button, {
    className: classList('Button Button--flat Button-emoji-parent', {
      active: liked
    }),
    title: title,
    "aria-label": title,
    "data-count": count,
    onclick: () => {
      if (post.canLike()) {
        toggleLike(post);
      } else if (PostLikesModal && app.forum.attribute('canSearchUsers')) {
        app.modal.show(PostLikesModal, {
          post
        });
      }
    }
  }, m("span", null, ReactionComponent ? m(ReactionComponent, {
    reaction: reaction,
    title: title
  }) : '👍'))));
}

/**
 * The likes part: it changes what flarum/likes adds to posts, so it runs
 * after flarum/likes (in the theme's late initializer).
 */
export function likesAsReaction() {
  extend(CommentPost.prototype, 'footerItems', function (items) {
    const reaction = likeReaction();
    const post = this.attrs.post;
    if (!reaction || post.isHidden()) return;
    if (items.has('liked')) items.remove('liked');
    const pill = likePill(post, reaction);

    // Before fof's reaction pills (10), in the same row.
    if (pill) items.add('bdLike', pill, 15);
  });

  // flarum/likes' Like button changes the loaded likes at once but not the
  // count, which the pill shows: keep it in step until the save comes back.
  extend(CommentPost.prototype, 'actionItems', function (items) {
    if (!items.has('like') || !likeReaction()) return;
    const post = this.attrs.post;
    const button = items.get('like');
    const onclick = button?.attrs?.onclick;
    if (!onclick) return;
    button.attrs.onclick = e => {
      const wasLiked = likedByMe(post);
      onclick(e);
      post.pushAttributes({
        likesCount: Math.max(0, (post.likesCount() || 0) + (wasLiked ? -1 : 1))
      });
    };
  });
  override('ext:fof/reactions/forum/components/PostReactAction', 'react', function (original, reaction, e) {
    const like = likeReaction();
    const post = this.attrs.post;
    if (!like || reaction.id() !== like.id() || !post.canLike()) return original(reaction, e);
    e?.target?.blur();
    toggleLike(post);
  });
}